import { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Dropdown, Popover } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import { LuChevronDown, LuChevronRight, LuClipboardCheck, LuEye, LuLogOut, LuSearch, LuUndo2, LuUser } from 'react-icons/lu';
import { useLogout, useUser } from '../../../config/connection/auth';
import { LOGIN_ROUTE } from '../../../config';
import { SidebarContext } from '../../../providers/SidebarProvider';
import { useReportContext } from '../../../providers/ReportContext';
import { AuthUser } from '../../../features/auth';
import { Avatar } from '../../Elements/Avatar';
import { itemDaRota } from './SidebarData';
import { ProgressoNoCabecalho } from './ProgressoNoCabecalho';
import { SinoDeAvisos } from '../../../features/avisos/components/AvisosDeMudanca';
import { temPoderDeAdm, usePerfil } from '../../../features/auth/hooks/usePerfil';
import { useVerComo } from '../../../stores/verComo';

/** Paginas que ficam "dentro" de um item da barra e ganham um degrau no caminho. */
const SUBPAGINAS: Record<string, string> = {
    report: 'Relatório de Estoque Ideal',
    'report-rem': 'Relatório de Remarcação',
};

function Header() {
    const { isOpen } = useContext(SidebarContext);
    const { isGeneratingReport, resumoHeader, acoesHeader } = useReportContext();
    const navigate = useNavigate();
    const location = useLocation();
    const logout = useLogout();
    const auth = useUser();

    const usuario = auth.data as AuthUser | undefined;
    const nome = usuario?.name || 'Usuário';

    /*
     * "Ver como lojista/comprador" (dono e adm, 07/10/2026): o site inteiro, tela e back, passa a ser
     * o daquele cargo. Trocar recarrega a página, para nada do cargo anterior ficar na tela; voltar
     * vai ao Dashboard (a página aberta pode ser de outro cargo).
     */
    const { perfilReal, verComo } = usePerfil();
    const setVerComo = useVerComo(estado => estado.setVerComo);
    const podeVerComo = temPoderDeAdm(perfilReal);
    const NOME_DO_CARGO = { lojista: 'lojista', comprador: 'comprador' } as const;
    const trocarCargo = (cargo: 'lojista' | 'comprador' | null) => {
        setVerComo(cargo);
        if (cargo) window.location.reload();
        else window.location.assign('/app/dashboard');
    };

    const exit = async () => {
        await logout.mutateAsync({});
        navigate(LOGIN_ROUTE);
    }

    const item = itemDaRota(location.pathname);
    const subpagina = SUBPAGINAS[location.pathname.replace(/^\/app\//, '').split('/')[0]];

    return (
        <header className={`header${isOpen ? '' : ' header--recolhido'}`}>
            <div className={resumoHeader || acoesHeader ? 'header-left header-left--resumo' : 'header-left'}>
                {/*
                  * Com o resumo rolado para fora da tela, os totais ocupam o
                  * lugar do caminho da pagina: e a informacao que o usuario precisa
                  * ter a mao enquanto percorre a tabela.
                  */}
                {acoesHeader
                    ? <div className="header-acoes">{acoesHeader}</div>
                    : resumoHeader
                    ? (
                        <div className="header-resumo">
                            {/* A remarcacao nao tem graficos de participacao; sem isto o
                              * botao subiria para abrir um balao vazio. */}
                            {resumoHeader.graficos && (
                                <Popover
                                    content={resumoHeader.graficos}
                                    trigger="click"
                                    placement="bottomLeft"
                                    overlayClassName="resumo-graficos-popover"
                                >
                                    {/* Só o ícone aqui: o texto tomava o espaço dos totais (07/10/2026). */}
                                    <button type="button" className="resumo-graficos-trigger hoverable" aria-label="Gráficos" title="Gráficos">
                                        <BarChartOutlined />
                                    </button>
                                </Popover>
                            )}
                            {resumoHeader.totais.map(item => (
                                <div className="header-resumo-item" key={item.label}>
                                    <span className="header-resumo-label">{item.label}</span>
                                    {/* O original vai no canto superior direito do valor, como nas celulas. */}
                                    <span className="header-resumo-valor">
                                        <span>{item.valor}</span>
                                        {item.original && <small className="resumo-original" title={`Projeção original: ${item.original}`}>{item.original}</small>}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )
                    : (
                        <div className="header-pagina">
                            {item && <span className="header-pagina-icone" aria-hidden="true">{item.icon}</span>}
                            <div className="header-pagina-textos">
                                {/* Acima do titulo, de onde a pagina vem: o item da barra, ou a marca. */}
                                <nav className="header-caminho" aria-label="Você está em">
                                    {subpagina && item ? <>
                                        <Link to={`/app/${item.link}`} className="header-caminho-link">{item.title}</Link>
                                        <LuChevronRight className="header-caminho-seta" aria-hidden="true" />
                                    </> : (
                                        <span>Rico</span>
                                    )}
                                </nav>
                                <span className="header-pagina-titulo" aria-current="page">
                                    {subpagina ?? item?.title ?? 'Rico'}
                                </span>
                            </div>
                        </div>
                    )}
            </div>

            <div className="header-direita">
                {/* Leva ao dashboard, que e onde o progresso do relatorio aparece. */}
                {isGeneratingReport && <>
                    <ProgressoNoCabecalho />
                    <span className="header-divisor" aria-hidden="true" />
                </>}

                {/* Lembrete de que a tela está como outro cargo, com a volta a um clique. */}
                {verComo && (
                    <span className="header-ver-como">
                        <LuEye /> Vendo como <b>{NOME_DO_CARGO[verComo]}</b>
                        <button type="button" onClick={() => trocarCargo(null)}>Voltar</button>
                    </span>
                )}

                {/* Central de notificações: mudanças em compras/lotes já enviados. */}
                <SinoDeAvisos />

                <Dropdown
                    trigger={['click']}
                    placement="bottomRight"
                    menu={{
                        items: [
                            {
                                key: 'conta',
                                type: 'group',
                                label: (
                                    <div className="header-conta">
                                        <strong>{nome}</strong>
                                        {usuario?.email && <span>{usuario.email}</span>}
                                    </div>
                                ),
                            },
                            { type: 'divider' },
                            ...(podeVerComo ? [
                                {
                                    key: 'ver-como', type: 'group' as const, label: 'Ver a tela como',
                                    children: [
                                        { key: 'ver-lojista', label: 'Lojista', icon: <LuClipboardCheck />, disabled: verComo === 'lojista' },
                                        { key: 'ver-comprador', label: 'Comprador', icon: <LuSearch />, disabled: verComo === 'comprador' },
                                        ...(verComo ? [{ key: 'ver-proprio', label: 'Voltar ao meu cargo', icon: <LuUndo2 /> }] : []),
                                    ],
                                },
                                { type: 'divider' as const },
                            ] : []),
                            { key: 'perfil', label: 'Meu perfil', icon: <LuUser /> },
                            { key: 'sair', label: 'Sair', icon: <LuLogOut />, danger: true },
                        ],
                        onClick: ({ key }) => {
                            if (key === 'sair') return exit();
                            if (key === 'ver-lojista') return trocarCargo('lojista');
                            if (key === 'ver-comprador') return trocarCargo('comprador');
                            if (key === 'ver-proprio') return trocarCargo(null);
                            navigate('/app/profile');
                        },
                    }}
                >
                    <button type="button" className="header-usuario" aria-label="Menu da conta">
                        <Avatar name={nome} size={30}className="header-usuario-avatar" />
                        <span className="header-usuario-textos">
                            <span className="header-usuario-nome">{nome}</span>
                            {usuario?.email && <span className="header-usuario-email">{usuario.email}</span>}
                        </span>
                        <LuChevronDown className="header-usuario-seta" />
                    </button>
                </Dropdown>
            </div>
        </header>
    )
}

export default Header;
